import React from 'react';
import { X, ChevronRight, Settings } from 'lucide-react';
import { C } from '../../styles/tokens';

/**
 * Billing modal — opened from CurrentPlanCard's "Billing" button.
 *
 * The POC mocks "Payment methods" / "Billing address" / "Invoices" as separate in-modal tabs
 * with fake forms, but this app hands all of that to Chargebee's hosted portal
 * (openPortal/useBilling.js) — it's PCI scope we don't want to take on ourselves. Those three
 * actions live as "Quick actions" links right here in the overview instead of separate tabs, so
 * there's just the one view.
 */
export default function BillingModal({
  onClose,
  planName,
  creditsUsed,
  creditsTotal,
  resetDate,
  statusInfo,
  StatusIcon,
  statusLabel,
  isFree,
  nextPayment,
  lastPayment,
  onOpenPortal,
  onManagePlans,
  canCancel,
  onCancelClick,
}) {
  const creditsRemaining = Math.max(creditsTotal - creditsUsed, 0);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 1050,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.w, borderRadius: 16, width: 540, maxWidth: '100%', maxHeight: '86vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,.25)',
          position: 'relative', boxSizing: 'border-box', overflow: 'hidden',
        }}
      >
        <div style={{ padding: '20px 22px 0', flexShrink: 0 }}>
          <button
            type="button"
            onClick={onClose}
            style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex' }}
          >
            <X size={16} color={C.n500} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: C.n100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Settings size={16} color={C.n900} />
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.n900, letterSpacing: '-.01em' }}>Billing</div>
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${C.n200}` }} />

        <div style={{ padding: '18px 22px 20px', overflowY: 'auto', flex: 1 }}>
          <p style={{ fontSize: 13, color: C.n600, marginBottom: 16 }}>
            This workspace is on the <strong style={{ color: C.n900 }}>{planName}</strong> plan.
          </p>

          <div style={{ border: `1px solid ${C.n200}`, borderRadius: 11, marginBottom: 16, overflow: 'hidden' }}>
            {[
              ['Current plan', planName],
              ['Credits remaining', `${creditsRemaining.toLocaleString()} / ${creditsTotal.toLocaleString()} credits`],
              ...(resetDate ? [['Credits reset date', resetDate]] : []),
            ].map(([label, val], i, arr) => (
              <div
                key={label}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px',
                  borderBottom: i < arr.length - 1 ? `1px solid ${C.n100}` : 'none',
                }}
              >
                <span style={{ fontSize: 12.5, color: C.n500 }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.n900 }}>{val}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div style={{ border: `1px solid ${C.n200}`, borderRadius: 11, padding: '14px 15px' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.n900, marginBottom: 12 }}>Billing details</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.n100}` }}>
                <span style={{ fontSize: 12, color: C.n500 }}>Subscription status</span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px',
                  background: statusInfo.bg, border: `1px solid ${statusInfo.border}`, borderRadius: 999,
                  fontSize: 11, fontWeight: 700, color: statusInfo.color,
                }}>
                  <StatusIcon size={9} />
                  {statusLabel}
                </span>
              </div>
              {!isFree && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.n100}` }}>
                    <span style={{ fontSize: 12, color: C.n500 }}>Next payment</span>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: C.n900 }}>
                      {nextPayment?.date ?? '—'}
                      {nextPayment?.amount && <span style={{ color: C.p600, marginLeft: 5 }}>{nextPayment.amount}</span>}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span style={{ fontSize: 12, color: C.n500 }}>Last payment</span>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: C.n900 }}>
                      {lastPayment?.date ?? '—'}
                      {lastPayment?.amount && <span style={{ color: C.g600, marginLeft: 5 }}>{lastPayment.amount}</span>}
                    </span>
                  </div>
                </>
              )}
            </div>
            <div style={{ border: `1px solid ${C.n200}`, borderRadius: 11, padding: '14px 15px', display: 'flex', flexDirection: 'column', gap: 7 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.n900, marginBottom: 5 }}>Quick actions</div>
              {[
                ['Invoice history', 'BILLING_HISTORY'],
                ['Change payment method', 'PAYMENT_SOURCES'],
                ['Change billing address', 'EDIT_BILLING_ADDRESS'],
              ].map(([label, section]) => (
                <button
                  key={section}
                  type="button"
                  onClick={() => onOpenPortal(section)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 11px',
                    background: C.n50, border: `1px solid ${C.n200}`, borderRadius: 9,
                    fontSize: 12, fontWeight: 600, color: C.n800, cursor: 'pointer',
                  }}
                >
                  {label}
                  <ChevronRight size={11} color={C.n400} />
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: canCancel ? 16 : 0 }}>
            <button
              type="button"
              onClick={() => { onClose(); onManagePlans?.(); }}
              style={{ flex: 1, padding: '10px', background: C.n900, color: C.w, border: 'none', borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
            >
              View plans
            </button>
            <button
              type="button"
              onClick={() => onOpenPortal('BILLING_HISTORY')}
              style={{ flex: 1, padding: '10px', background: C.w, color: C.n900, border: `1px solid ${C.n300}`, borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
            >
              View invoices
            </button>
          </div>

          {canCancel && (
            <button
              type="button"
              onClick={() => { onClose(); onCancelClick(); }}
              style={{
                width: '100%', padding: '10px', background: '#FEF2F2', color: '#B91C1C',
                border: '1px solid #FECACA', borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Cancel subscription
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
